import { LegalPage, type LegalSection } from "#/components/legal/LegalPage.js";

type Props = { locale: string };

const TITLE: Record<string, string> = {
  en: "Terms of use",
  ar: "شروط الاستخدام",
  fr: "Conditions d'utilisation",
  "zh-CN": "Terms of use",
  es: "Condiciones de uso",
  de: "Nutzungsbedingungen",
  ru: "Terms of use",
  hi: "Terms of use",
  id: "Ketentuan penggunaan",
  ms: "Terma penggunaan",
};

const INTRO: Record<string, string> = {
  en: "These terms govern use of the Hantavirus public outbreak tracker, including the website, installable PWA, public dashboards, email digests, and notification features.",
  ar: "تنظم هذه الشروط استخدام متتبع تفشي فيروس هانتا العام، بما في ذلك الموقع وتطبيق الويب التقدمي ولوحات المعلومات العامة ورسائل البريد الإلكتروني والتنبيهات.",
  fr: "Ces conditions régissent l'utilisation du suivi public des épidémies à hantavirus, y compris le site, la PWA, les tableaux de bord publics, les e-mails et les notifications.",
};

const UPDATED_LABEL: Record<string, string> = {
  en: "Last updated",
  ar: "آخر تحديث",
  fr: "Dernière mise à jour",
};

const SECTIONS: LegalSection[] = [
  {
    heading: "Informational use only",
    body: "The service aggregates public hantavirus surveillance information for awareness, research, journalism, and public-health monitoring. It is not medical advice, diagnosis, treatment, emergency guidance, or an official government alert system.",
  },
  {
    heading: "Seek professional help",
    body: "If you may have been exposed to rodents or a confirmed hantavirus case and have fever, breathing difficulty, low urine output, bleeding, confusion, or rapidly worsening symptoms, contact emergency medical services or a qualified health professional immediately.",
  },
  {
    heading: "Data accuracy",
    items: [
      "We work to cite source URLs and verification timestamps, but public outbreak data can be delayed, revised, incomplete, translated, or contradicted by later reports.",
      "Maps, counts, risk labels, ship positions, and timelines may lag behind official decisions or field conditions.",
      "Do not rely on this service as the only basis for travel, medical, operational, or public-safety decisions.",
    ],
  },
  {
    heading: "Acceptable use",
    items: [
      "Do not interfere with the service, scrape at abusive rates, probe security controls, or attempt unauthorized access.",
      "Do not use the service to harass individuals, identify private patients, publish personal health information, or spread misleading outbreak claims.",
      "Public links may be shared with attribution, but source agencies and data providers retain their own rights and terms.",
    ],
  },
  {
    heading: "Subscriptions and notifications",
    body: "Email digests, push alerts, and PWA notifications are best-effort convenience features. Delivery can fail or be delayed by network conditions, browser settings, email providers, or device power-saving behavior.",
  },
  {
    heading: "Third-party sources",
    body: "The service links to public-health agencies, data providers, news organizations, and maritime services. Those third parties are independent from Hantavirus and may change, remove, or restrict their content at any time.",
  },
  {
    heading: "No warranties",
    body: "The service is provided as is and as available. We do not warrant that it will be uninterrupted, error-free, complete, timely, or suitable for any specific medical, legal, operational, or commercial purpose.",
  },
  {
    heading: "Limitation of liability",
    body: "To the fullest extent allowed by law, Hantavirus and its operators are not liable for losses arising from use of, inability to use, or reliance on the service or on third-party information linked from it.",
  },
  {
    heading: "Changes",
    body: "We may update the service and these terms as the project changes. Continued use after an update means you accept the revised terms.",
  },
  {
    heading: "Contact",
    body: "For terms, attribution, or data accuracy questions, contact privacy@hantavirus.app or data@hantavirus.app with the relevant page URL and details.",
  },
];

export { Page };

function Page({ locale }: Props) {
  return (
    <LegalPage
      title={TITLE[locale] ?? "Terms of use"}
      intro={
        INTRO[locale] ??
        "These terms govern use of the Hantavirus public outbreak tracker, including the website, installable PWA, public dashboards, email digests, and notification features."
      }
      updatedLabel={UPDATED_LABEL[locale] ?? "Last updated"}
      updatedValue="13 May 2026"
      sections={SECTIONS}
    />
  );
}
